import { useForm, Controller } from "react-hook-form";
import { IColaboradorFormRHF } from "../../../types/colaborador";
import { TipoColaboradorEnum } from "../../../enums/tipoColaborador";
import { useCallback, useEffect, useState } from "react";
import { maskCpf, maskPhone } from "../../../utils/masks";
import { ISelectOptions } from "../../../types/inputs";
import { prettyNamePerfil, removeUnderscore } from "../../../utils/removeUnderscore";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loja } from "../../../services/Lojas";
import { toast } from "react-toastify";
import { Delivery } from "../../../services/Delivery";
import { Perfil } from "../../../services/Perfil";

const tipoOptions = Object.values(TipoColaboradorEnum).map((e) => ({
  value: e,
  label: removeUnderscore(e),
}));

const schemaPerfil = z.object({
  value: z.string(),
  label: z.string(),
});

const schema = z.object({
  cpf: z.string().min(14, "CPF invalido"),
  nome: z.string().min(1, "Campo obrigatorio"),
  email: z.string().email("Email invalido").optional().or(z.literal("")),
  uuidDelivery: z.string().optional().or(z.literal("")),
  uuidLoja: z.string().optional().or(z.literal("")),
  telefone: z.string().min(11, "Telefone invalido"),
  tipo: z.string().min(1, "Campo obrigatorio"),
  perfis: z.array(schemaPerfil, { message: "Campo obrigatorio" }),
});

export const useFormCollaboratorRegister = () => {
  const [perfis, setPerfis] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const [lojas, setLojas] = useState<ISelectOptions[]>([] as ISelectOptions[]);
  const [deliverys, setDeliverys] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IColaboradorFormRHF>({
    defaultValues: {
      cpf: "",
      nome: "",
      email: "",
      telefone: "",
      uuidDelivery: "",
      uuidLoja: "",
      tipo: "" as TipoColaboradorEnum,
      perfis: [],
    },
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  const getLojas = useCallback(() => {
    Loja.get()
      .then(({ data }) => {
        const options = data.content.map((i) => ({
          value: i.uuid,
          label: i.nome,
          element: i,
        }));
        setLojas(options);
      })
      .catch(
        ({
          response: {
            data: { messagem },
          },
        }) => toast.error(messagem)
      );
  }, []);

  const getDeliverys = useCallback(() => {
    Delivery.get()
      .then(({ data }) => {
        const options = data.content.map((i) => ({
          value: i.uuid,
          label: i.cidade,
          element: i,
        }));
        setDeliverys(options);
      })
      .catch(
        ({
          response: {
            data: { messagem },
          },
        }) => toast.error(messagem)
      );
  }, []);

  const getPerfis = useCallback(() => {
    Perfil.get({incluirAdmin: false, incluirInativos: false}).then(({ data }) => {
      const options = data.content.map((i) => ({
        value: i.nome,
        label: removeUnderscore(prettyNamePerfil(i.nome)),
        element: i,
      }));

      setPerfis(options);
    });
  }, []);

  useEffect(() => {
    if (watch("cpf")) {
      setValue("cpf", maskCpf(watch("cpf")));
    }
  }, [watch("cpf")]);

  useEffect(() => {
    if (watch("telefone")) {
      setValue("telefone", maskPhone(watch("telefone")));
    }
  }, [watch("telefone")]);

  useEffect(() => {
    getPerfis();
    getLojas();
    getDeliverys();
  }, []);

  return {
    handleSubmit,
    register,
    control,
    perfis,
    tipoOptions,
    Controller,
    errors,
    lojas,
    deliverys,
  };
};
